import { useEffect, useState, useRef } from 'react';
import request from '../../utils/request';
import Select from '../../components/Select/Select';
import './EditEmployeeModal.css';

function isInsideModalContent(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false;
  return target.closest('.modal-content') !== null;
}

interface EmployeeItem {
  id: number;
  name: string;
  email: string;
  role_id: number;
  status: number;
}

interface EditEmployeeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  employeeData: EmployeeItem | null;
}

interface RoleItem {
  id: number;
  name: string;
  status: number;
}

interface ApiResponse<T> {
  code: number;
  msg: string;
  data: T;
}

interface RoleListData {
  items: RoleItem[];
  total: number;
}

export default function EditEmployeeModal({
  isOpen,
  onClose,
  onSuccess,
  employeeData,
}: EditEmployeeModalProps) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    role_id: 0,
    status: 1,
  });
  const [roles, setRoles] = useState<RoleItem[]>([]);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const overlayRef = useRef<HTMLDivElement>(null);
  const mouseDownInsideContent = useRef(false);

  // Load role list
  useEffect(() => {
    if (isOpen) {
      fetchRoles();
    }
  }, [isOpen]);

  const fetchRoles = async () => {
    try {
      setRolesLoading(true);
      const result = (await request.get('/gradmin/roles')) as ApiResponse<RoleListData>;
      // Only show enabled roles
      const enabledRoles = result.data.items.filter((role: RoleItem) => Number(role.status) === 1);
      setRoles(enabledRoles);
    } catch (err) {
      console.error('Failed to fetch role list:', err);
    } finally {
      setRolesLoading(false);
    }
  };

  useEffect(() => {
    if (employeeData) {
      setFormData({
        name: employeeData.name,
        email: employeeData.email,
        role_id: employeeData.role_id,
        status: employeeData.status,
      });
    }
  }, [employeeData]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!employeeData) return;

    // Validate role selection
    if (formData.role_id === 0) {
      setError('Please select a role');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await request.put(`/gradmin/employees/${employeeData.id}`, formData);
      onSuccess();
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Update failed');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setError('');
      onClose();
    }
  };

  if (!isOpen || !employeeData) return null;

  return (
    <div
      ref={overlayRef}
      className="modal-overlay"
      onMouseDown={e => {
        mouseDownInsideContent.current = isInsideModalContent(e.target);
      }}
      onClick={e => {
        if (e.target === overlayRef.current && !mouseDownInsideContent.current) {
          handleClose();
        }
      }}
    >
      <div className="modal-content">
        <div className="modal-header">
          <h2 className="modal-title">Edit Employee</h2>
          <button className="modal-close" onClick={handleClose} disabled={loading}>
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M18 6 6 18" />
              <path d="m6 6 12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {error && <div className="modal-error-alert">{error}</div>}

            <div className="modal-form-group">
              <label className="modal-form-label">
                Name <span className="modal-required">*</span>
              </label>
              <input
                type="text"
                className="modal-form-input"
                value={formData.name}
                onChange={e => setFormData({ ...formData, name: e.target.value })}
                placeholder="Enter employee name"
                required
                disabled={loading}
              />
            </div>

            <div className="modal-form-group">
              <label className="modal-form-label">
                Email <span className="modal-required">*</span>
              </label>
              <input
                type="email"
                className="modal-form-input"
                value={formData.email}
                onChange={e => setFormData({ ...formData, email: e.target.value })}
                placeholder="Enter email address"
                required
                disabled={loading}
              />
            </div>

            <div className="modal-form-group">
              <label className="modal-form-label">
                Role <span className="modal-required">*</span>
              </label>
              <Select
                value={formData.role_id || undefined}
                options={roles.map(role => ({ value: role.id, label: role.name }))}
                placeholder="Select a role"
                onChange={value => setFormData({ ...formData, role_id: Number(value) })}
                disabled={loading || rolesLoading}
              />
            </div>

            <div className="modal-form-group">
              <label className="modal-form-label">Status</label>
              <div className="modal-radio-group">
                <label className="modal-radio-label">
                  <input
                    type="radio"
                    name="status"
                    value={1}
                    checked={formData.status === 1}
                    onChange={e => setFormData({ ...formData, status: Number(e.target.value) })}
                    disabled={loading}
                  />
                  <span>Active</span>
                </label>
                <label className="modal-radio-label">
                  <input
                    type="radio"
                    name="status"
                    value={2}
                    checked={formData.status === 2}
                    onChange={e => setFormData({ ...formData, status: Number(e.target.value) })}
                    disabled={loading}
                  />
                  <span>Disabled</span>
                </label>
              </div>
            </div>
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="modal-btn modal-btn-secondary"
              onClick={handleClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button type="submit" className="modal-btn modal-btn-primary" disabled={loading}>
              {loading ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
